import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { cx, apiKey } from '../../GoogleSearch/GoogleSearch';
import { encryptData } from '../../encryption-sdk';
import config from '../../../config';
import './MyStylistPage.css';
import { useAuth } from '../../PersonalReport/Utils/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../PersonalReport/LoadingSpinner';
import { parseReportJson, useReportContext } from '../../ReportContext';
import FeedbackPanel from './FeedbackPanel';
import MobileFeedbackPanel from './MobileFeedbackPanel';
import { logger } from '../../../utils/logger';
import { usePaymentIntent } from '../../PersonalReport/Utils/PaymentIntentContext';

interface InspirationGalleryProps {
  gender: 'Male' | 'Female';
  onCustomize: () => void;
}

const femaleFashionStyles = [
  { name: 'Casual', image: '/casual2.webp' },
  { name: 'Minimalist', image: '/minimalist.webp' },
  { name: 'Classic', image: '/classic.webp' },
  { name: 'Athleisure', image: '/sports.webp' },
  { name: 'Bohemian', image: '/bohemian.webp' },
  { name: 'Street', image: '/street3.webp' },
  { name: 'Rock', image: '/rock.webp' },
  { name: 'Vintage', image: '/vintage_black.webp' },
  { name: 'Preppy', image: '/preppy.webp' },
  { name: 'Resort', image: '/resort.webp' }
];

const maleFashionStyles = [
  { name: 'Smart Casual', image: '/smart_casual.webp' },
  { name: 'Workwear', image: '/workwearMan.webp' },
  { name: 'Vintage', image: '/vintageMan.webp' },
  { name: 'Streetwear', image: '/streetman.webp' },
  { name: 'Athleisure', image: '/sportsMan.webp' },
  { name: 'Business Formal', image: '/businessFormal.webp' },
  { name: 'Resort', image: '/resortMan_glass.webp' },
  { name: 'Casual', image: '/casual_12.webp' },
];

// Responsive slidesPerView and effect
const getSwiperConfig = () => {
  if (window.innerWidth < 900) { // mobile
    return {
      slidesPerView: 1.8,
      effect: 'coverflow',
      centeredSlides: true,
      loop: true,
      spaceBetween: 16,
      modules: [EffectCoverflow],
      coverflowEffect: {
        rotate: 18,
        stretch: 0,
        depth: 120,
        modifier: 1,
        slideShadows: true,
      },
      highlight: true,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
    };
  } else { // desktop
    return {
      slidesPerView: 5,
      effect: 'coverflow',
      centeredSlides: true,
      loop: true,
      modules: [EffectCoverflow],
      coverflowEffect: {
        rotate: 12,
        stretch: 0,
        depth: 120,
        modifier: 1,
        slideShadows: true,
      },
      highlight: false,
      slidesOffsetBefore: 32,
      slidesOffsetAfter: 48,
    };
  }
};

// 严格复用 ChatPage 的过滤敏感词函数
function filterSensitiveWords(text: string): string {
  const sensitiveWords = ['fuck', 'shit', 'bitch', 'ass', 'dick', 'pussy', 'cock', 'cunt'];
  let filteredText = text;
  sensitiveWords.forEach(word => {
    const regex = new RegExp(word, 'gi');
    filteredText = filteredText.replace(regex, '*'.repeat(word.length));
  });
  return filteredText;
}

// 严格复用 ChatPage 的 Google 搜索 URL 生成函数
const generateGoogleSearchUrl = (itemName: string) => {
  return `https://www.google.com/search?q=${encodeURIComponent(itemName)}`;
};

// Pexels API Key（建议放到安全的地方）
const PEXELS_API_KEY = process.env.REACT_APP_PEXELS_API_KEY || '';

const fetchPexelsImageUrl = async (itemName: string): Promise<string> => {
  try {
    const searchQuery = encodeURIComponent(itemName);
    const pexelsApiUrl = `https://api.pexels.com/v1/search?query=${searchQuery}&per_page=1`;
    const response = await fetch(pexelsApiUrl, {
      headers: {
        Authorization: PEXELS_API_KEY,
      },
    });
    const data = await response.json();
    if (data.photos && data.photos.length > 0) {
      return data.photos[0].src.medium; // 或 original
    }
  } catch (error) {
    logger.error('Error fetching product image from Pexels:', error);
  }
  return 'https://dummyimage.com/600x400/000/fff&text=No+Image';
};

// 工具函数：去除所有 **、__、[ ]
function stripMarkdownFormatting(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/^\[([^\]]+)\]$/g, '$1') // 去掉整个内容被[]包裹的情况
    .replace(/\[([^\]]+)\]/g, '$1');  // 去掉内容中所有[]包裹的部分
}

// 严格复用 ChatPage 的商品列表生成函数
const generateShoppingList = async (itemListText: string) => {
  if (!itemListText) {
    logger.log("No items found in the recommendation text.");
    return [];
  }

  const items = itemListText.split(/\n|,/)
    .map(item => item.trim().replace(/(^["-]+|["]+$)/g, ''))
    .filter(item => item && item.includes(":"))
    .map(item => {
      const [itemName, itemDescription] = item.split(":").map(part => part.trim());
      return { 
        itemName: stripMarkdownFormatting(itemName), 
        itemDescription: stripMarkdownFormatting(itemDescription) 
      };
    });

  const shoppingList = await Promise.all(items.map(async (item) => {
    const googleSearchUrl = generateGoogleSearchUrl(item.itemName);
    const imageUrl = await fetchPexelsImageUrl(item.itemName); // 使用 Pexels 获取图片

    return {
      itemName: item.itemName,
      itemDescription: item.itemDescription,
      googleSearchUrl, // Google 搜索链接
      imageUrl, // 从 Pexels 获取到的图片链接
    };
  }));

  return shoppingList;
};

const loadingSteps = [
  'Generating inspiration... ✨',
  'Curating your look... 🧢',
  'Fetching shopping items... 🛍️',
];

const InspirationGallery: React.FC<InspirationGalleryProps> = ({ gender, onCustomize }) => {
  const { email: rawEmail } = useAuth();
  const email = useMemo(() => rawEmail, [rawEmail]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [swiperConfig, setSwiperConfig] = useState(getSwiperConfig());
  const [showGuide, setShowGuide] = useState(true);
  const [isSliding, setIsSliding] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastStage, setToastStage] = useState<'skeleton' | 'progress' | 'content' | 'shopping'>('skeleton');
  const [recommendReady, setRecommendReady] = useState(false);
  const toastTimer = React.useRef<NodeJS.Timeout | null>(null);
  const [hoveredCardIndex, setHoveredCardIndex] = useState<number | null>(null);
  const [recommendation, setRecommendation] = useState('');
  const [shoppingList, setShoppingList] = useState<{ itemName: string; itemDescription: string; googleSearchUrl: string; imageUrl: string }[]>([]);
  const initialResponsesRef = useRef<any[]>([]);
  const navigate = useNavigate();
  const [loadingStep, setLoadingStep] = useState(0);
  const loadingStepText = loadingSteps[loadingStep];
  const [isLocked, setIsLocked] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showMobileFeedback, setShowMobileFeedback] = useState(false);
  const { isPaymentIntentModalOpen, recordRecommendationInteraction } = usePaymentIntent();

  // Local state: null means not manually switched
  const [selectedGender, setSelectedGender] = React.useState<'Male' | 'Female'>(gender);

  // Effective gender
  const fashionStyles = selectedGender === 'Male' ? maleFashionStyles : femaleFashionStyles;

  useEffect(() => {
    const handleResize = () => setSwiperConfig(getSwiperConfig());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // 首次进入时显示引导，2秒后自动消失
    setShowGuide(true);
    const timer = setTimeout(() => setShowGuide(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (showToast) {
      document.body.classList.add('toast-open');
    } else {
      document.body.classList.remove('toast-open');
    }
    return () => document.body.classList.remove('toast-open');
  }, [showToast]);

  useEffect(() => {
    if ((toastStage === 'skeleton' || toastStage === 'progress') && showToast) {
      setLoadingStep(0);
      const timers = [
        setTimeout(() => setLoadingStep(1), 3000),
        setTimeout(() => setLoadingStep(2), 6000),
      ];
      return () => timers.forEach(clearTimeout);
    }
  }, [toastStage, showToast]);

  // 页面锁定：gallery界面永远不滚动
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // InspirationGallery 组件内
  useEffect(() => {
    (document.body.style as any).zoom = '1';
    return () => {
      (document.body.style as any).zoom = '';
    };
  }, []);

  const handleSlideChange = (swiper: any) => {
    setActiveIndex(swiper.activeIndex);
  };

  const handleCardClick = async (idx: number) => {
    // 支付意愿调研弹窗打开时，阻止所有推荐交互
    if (isPaymentIntentModalOpen) return;

    // 记录一次 recommendation 交互（第 3 次会在全局触发弹窗）
    const result = recordRecommendationInteraction('gallery_card_click');
    if (result === 'threshold_reached') {
      // 第 3 次点击：立刻弹窗，本次不继续推荐
      return;
    }

    if (isLocked) return;
    setIsLocked(true);
    setActiveIndex(idx);
    setShowToast(true);
    setToastStage('progress');
    setRecommendReady(false);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    const styleName = fashionStyles[idx].name;
    setRecommendation('');
    setShoppingList([]);
    initialResponsesRef.current = [
      { question: 'style', answer: `I'm looking for ${styleName} style inspiration. My gender is ${selectedGender}.` }
    ];
    try {
      const payload = {
        email: JSON.stringify(email),
        responses: initialResponsesRef.current,
        isFollowUp: false
      };
      const encryptedBody = await encryptData(JSON.stringify(payload));
      const apiUrl = `${config.GPT_MODEL_URL}/modelD`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload) // 这里如需加密可改为 encryptedBody
      });
      const responseText = await response.text();
      let responseObj;
      try {
        responseObj = JSON.parse(responseText);
      } catch (e) {
        responseObj = { response: responseText };
      }
      const onlyResponse = responseObj.response || '';

      // 严格复用 ChatPage 的响应处理逻辑
      const safeResponse = filterSensitiveWords(onlyResponse);

      // 提取 "Item list" 部分
      const itemListMatch = safeResponse.match(/Item List:\s*([\s\S]*)/i);
      const extractedItemListText = itemListMatch ? itemListMatch[1].trim() : '';
      const filteredResponse = safeResponse.replace(/Item List:\s*([\s\S]*)/i, '').trim();
      const safeItemList = filterSensitiveWords(extractedItemListText);

      // 去除开头的 Recommendation Statement 标题
      let filteredResponseClean = filteredResponse;
      const recMatch = filteredResponse.match(/(?:\*\*|#+)?\s*Recommendation Statement[:：]*\s*([\s\S]*)/i);
      if (recMatch) {
        filteredResponseClean = recMatch[1].trim();
      }

      setRecommendation(stripMarkdownFormatting(filteredResponseClean));
      
      if (safeItemList) {
        const shopping = await generateShoppingList(safeItemList);
        setShoppingList(shopping);
      } else {
        logger.error('No item list available.');
        setShoppingList([]);
      }
      
      setRecommendReady(true);
      setToastStage('content');
    } catch (e) {
      setRecommendReady(true);
      setToastStage('content');
      setRecommendation('Sorry, there was an error fetching your recommendations.');
      setShoppingList([]);
    }
  };

  const handleSeeAnotherBatch = async () => {
    // 支付意愿调研弹窗打开时，不再继续请求
    if (isPaymentIntentModalOpen) return;

    // “See another batch” 也算一次推荐交互
    const result = recordRecommendationInteraction('gallery_see_another');
    if (result === 'threshold_reached') {
      // 第 3 次交互：直接弹窗，本次不继续请求
      return;
    }

    setShowToast(true);
    setToastStage('progress');
    setRecommendReady(false);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setRecommendation('');
    setShoppingList([]);
    // 基于当前 initialResponsesRef 追加强提示扰动项
    const refreshToken = Math.random().toString(36).slice(2) + Date.now();
    const newResponses = [
      ...initialResponsesRef.current,
      {
        question: 'refresh',
        answer: `Please generate a recommendation that is as different as possible from the previous one, even if the style is the same. Avoid repeating any previous ideas. [${refreshToken}]`
      }
    ];
    try {
      const payload = {
        email: JSON.stringify(email),
        responses: newResponses,
        isFollowUp: true
      };
      const encryptedBody = await encryptData(JSON.stringify(payload));
      const apiUrl = `${config.GPT_MODEL_URL}/modelD`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const responseText = await response.text();
      let responseObj;
      try {
        responseObj = JSON.parse(responseText);
      } catch (e) {
        responseObj = { response: responseText };
      }
      const onlyResponse = responseObj.response || '';
      const safeResponse = filterSensitiveWords(onlyResponse);
      const itemListMatch = safeResponse.match(/Item List:\s*([\s\S]*)/i);
      const extractedItemListText = itemListMatch ? itemListMatch[1].trim() : '';
      const filteredResponse = safeResponse.replace(/Item List:\s*([\s\S]*)/i, '').trim();
      const safeItemList = filterSensitiveWords(extractedItemListText);
      let filteredResponseClean = filteredResponse;
      const recMatch = filteredResponse.match(/(?:\*\*|#+)?\s*Recommendation Statement[:：]*\s*([\s\S]*)/i);
      if (recMatch) {
        filteredResponseClean = recMatch[1].trim();
      }
      setRecommendation(stripMarkdownFormatting(filteredResponseClean));
      if (safeItemList) {
        const shopping = await generateShoppingList(safeItemList);
        setShoppingList(shopping);
      } else {
        setShoppingList([]);
      }
      setRecommendReady(true);
      setToastStage('content');
      // 更新 initialResponsesRef
      initialResponsesRef.current = newResponses;
    } catch (e) {
      setRecommendReady(true);
      setToastStage('content');
      setRecommendation('Sorry, there was an error fetching your recommendations.');
      setShoppingList([]);
    }
  };

  const handleFeedbackSubmit = async (feedback: { like: boolean | null; favorite: boolean; comment: string }) => {
    try {
      const comment = `Reaction: ${feedback.like === true ? 'Like' : feedback.like === false ? 'Dislike' : ''}${feedback.favorite ? ' + Favorite' : ''}\n${feedback.comment}`;
      const body = {
        email,
        comment,
        context: recommendation, // the model API response for this recommendation
      };
      await fetch(`${(config as any).FEEDBACK_API_BASE_URL}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch (err) {
      // Optionally show error to user
    }
  };

  const currentStyle = fashionStyles[activeIndex];
  const isMobile = window.innerWidth < 900;

  // 弹窗关闭时解锁
  const handleToastClose = () => {
    setShowToast(false);
    setIsLocked(false);
  };

  return (
    <div className={`inspiration-gallery-swiper inspiration-gallery-padding ${!isMobile ? 'desktop' : ''}`}>
      <div className="inspiration-gallery-content">
        <h2 className="gallery-title">What's the vibe today?</h2>
        <button
          className={!isMobile ? 'desktop-gender-switch-fab' : 'mobile-gender-switch-fab'}
          onClick={() => setSelectedGender(selectedGender === 'Male' ? 'Female' : 'Male')}
          aria-label={selectedGender === 'Male' ? 'Switch to Female' : 'Switch to Male'}
        >
          {!isMobile
            ? (selectedGender === 'Male' ? 'Switch to Female version' : 'Switch to Male version')
            : (selectedGender === 'Male' ? 'F' : 'M')
          }
        </button>
        <div className="swipe-guide-container">
          {showGuide && <div className="swipe-guide">←  Swipe to explore  →</div>}
        </div>
        <Swiper
          spaceBetween={24}
          slidesPerView={swiperConfig.slidesPerView}
          effect={swiperConfig.effect}
          centeredSlides={swiperConfig.centeredSlides}
          loop={swiperConfig.loop}
          modules={swiperConfig.modules}
          coverflowEffect={swiperConfig.coverflowEffect}
          onSlideChange={handleSlideChange}
          className="gallery-swiper"
          slidesOffsetBefore={swiperConfig.slidesOffsetBefore}
          slidesOffsetAfter={swiperConfig.slidesOffsetAfter}
          onTransitionStart={() => setIsSliding(true)}
          onTransitionEnd={() => setIsSliding(false)}
          onTouchStart={() => setIsSliding(true)}
          onTouchEnd={() => setIsSliding(false)}
        >
          {fashionStyles.map((style, idx) => (
            <SwiperSlide key={style.name}>
              <div
                className={
                  (!isMobile && activeIndex === idx
                    ? 'gallery-card selected'
                    : 'gallery-card') +
                  (!isMobile && activeIndex === idx && isSliding ? ' is-sliding' : '')
                }
                onClick={() => handleCardClick(idx)}
                onMouseEnter={() => !isMobile && setHoveredCardIndex(idx)}
                onMouseLeave={() => !isMobile && setHoveredCardIndex(null)}
                style={isLocked ? { pointerEvents: 'none', opacity: 0.7 } : {}}
              >
                <img src={style.image} alt={style.name} className="gallery-image" />
                <div className="gallery-style-name">{style.name}</div>
                {!isMobile && hoveredCardIndex === idx && (
                  <div className="card-hover-tip">Click to see recommendation</div>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {isMobile && (
          <div className="tap-hint">
            Tap any card to explore
          </div>
        )}
        {/* desktop端：bubble按钮在swiper卡片下方偏右，mobile端吸底居中（toast未弹出时） */}
        {!showToast && (
          isMobile ? (
            <button
              className="mobile-bubble-btn"
              onClick={onCustomize}
            >
              <span className="bubble-btn-text">Start with more personalized details</span>
              <span className="bubble-btn-bubble bubble-bubble-1"></span>
              <span className="bubble-btn-bubble bubble-bubble-2"></span>
              <span className="bubble-btn-bubble bubble-bubble-3"></span>
            </button>
          ) : (
            <div className="desktop-bubble-container">
              <button
                className="desktop-bubble-btn"
                onClick={onCustomize}
              >
                <span className="bubble-btn-text">Start with more personalized details</span>
                <span className="bubble-btn-bubble bubble-bubble-1"></span>
                <span className="bubble-btn-bubble bubble-bubble-2"></span>
                <span className="bubble-btn-bubble bubble-bubble-3"></span>
              </button>
            </div>
          )
        )}
        {isMobile && <div className="mobile-spacer-bottom" />}
        {/* 推荐结果底部弹窗 - 严格复用 ChatPage 的样式和结构 */}
        {showToast && !isMobile && (
          <div className={`smart-toast desktop two-column`}>
            <div className="toast-left">
              {(toastStage === 'skeleton' || toastStage === 'progress') && (
                <div className="toast-skeleton">
                  <LoadingSpinner text={loadingStepText} />
                </div>
              )}
              {toastStage === 'content' && (
                <>
                  <h3>Recommended for you <span role="img" aria-label="sparkles">✨</span></h3>
                  <div className="recommend-result-content">{recommendation}</div>
                  <div className="toast-actions-container">
                    <button className="customize-btn" onClick={onCustomize}>Want to customize?</button>
                    <button className="refresh-btn" onClick={handleSeeAnotherBatch}>
                      <span role="img" aria-label="shuffle">🔄</span> See another batch
                    </button>
                  </div>
                </>
              )}
              {toastStage === 'shopping' && (
                <>
                  <h3>Your Shopping List <span role="img" aria-label="shopping">🛍️</span></h3>
                  <div className="shopping-list-content">
                    {shoppingList.map((item, index) => (
                      <div key={index} className="shopping-item-preview">
                        <img src={item.imageUrl} alt={item.itemName} className="shopping-item-preview-img" />
                        <div className="shopping-item-preview-info">
                          <p className="shopping-item-preview-name">{item.itemName}</p>
                          <p className="shopping-item-preview-desc">{item.itemDescription}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="toast-actions-container">
                    <button className="customize-btn" onClick={onCustomize}>Want to customize?</button>
                    <button className="refresh-btn" onClick={handleSeeAnotherBatch}>
                      <span role="img" aria-label="shuffle">🔄</span> See another batch
                    </button>
                  </div>
                </>
              )}
            </div>
            <div className="toast-right">
              <button className="toast-close-btn-inline" onClick={handleToastClose}>×</button>
              {shoppingList.length > 0 && (
                <>
                  <h3>Your Shopping List</h3>
                  <p className="shopping-hint">
                    Click an image to explore unlimited search results. Images shown are general and meant to spark inspiration.
                  </p>
                  <div className="shopping-list">
                    <div className="shopping-items">
                      {shoppingList.map((item, index) => (
                        <div key={index} className="shopping-item">
                          <a href={item.googleSearchUrl} target="_blank" rel="noopener noreferrer">
                            <img src={item.imageUrl} alt={item.itemName} className="desktop-shopping-item-img" />
                          </a>
                          <p>{item.itemName}</p>
                          <p>{item.itemDescription}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="fixed right-8 bottom-8 z-50">
              <FeedbackPanel onSubmit={handleFeedbackSubmit} />
            </div>
          </div>
        )}
       {showToast && isMobile && (
          <>
            {/* Mobile Feedback Symbols - fixed position, not inside toast */}
            <div className="mobile-feedback-symbols">
              <MobileFeedbackPanel />
            </div>
            <div className="mobile-toast">
              <button className="mobile-toast-close" onClick={handleToastClose}>×</button>
              <div className="mobile-toast-top">
                {toastStage === 'skeleton' || toastStage === 'progress' ? (
                  <LoadingSpinner text={loadingStepText} />
                ) : (
                  <>
                    <div className="mobile-toast-title">Recommended for you ✨</div>
                    <div className="mobile-toast-desc">{recommendation}</div>
                    <div className="mobile-toast-actions">
                      <button className="customize-btn" onClick={onCustomize}>Want to customize?</button>
                      <button className="refresh-btn" onClick={handleSeeAnotherBatch}>See another batch</button>
                    </div>
                  </>
                )}
              </div>
              {toastStage === 'content' && (
                <div className="mobile-toast-bottom">
                  {shoppingList.length > 0 && (
                    <p className="shopping-hint" style={{marginBottom: 8, color: '#b89b5c', fontSize: 13, textAlign: 'center'}}>
                      Click an image to explore unlimited search results. Images shown are general and meant to spark inspiration.
                    </p>
                  )}
                  <div className="mobile-toast-products">
                    {shoppingList.map((item, index) => (
                      <div key={index} className="mobile-toast-product">
                        <a href={item.googleSearchUrl} target="_blank" rel="noopener noreferrer">
                          <img src={item.imageUrl} alt={item.itemName} className="mobile-shopping-item-img" />
                        </a>
                        <div className="mobile-shopping-item-name">{item.itemName}</div>
                        <div className="mobile-shopping-item-desc">{item.itemDescription}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InspirationGallery; 